import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Dropdown,
  TextInput,
  InputStatuses,
  ButtonVariants,
  Tooltip,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
  TypographyElements,
} from '@carboledger/ui';
import { Info } from '@carboledger/ui/icons';
import { useTranslations } from '@/hooks';
import { REGEX } from '@/config/regex.constants';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import { formatDate } from '@/lib/format/date';
import { useLazyConvertUnitsQuery } from '@/lib/api/unitsApi';
import { needsDensity, normalizeUnit } from '@/lib/utils/unitConversion';
import styles from './PhysicalBalanceModal.module.scss';

// A safe stand-in before the modal has ever been opened with a row.
const EMPTY_ROW = { from: '', to: '', physicalClosingBalanceRaw: null, hasPhysicalBalance: false };

/**
 * Props:
 *   open       whether the modal is shown
 *   onClose    ✕ / Cancel
 *   row        the reconciliation row — its period, and the balance already saved, if any
 *   units      { declaration, working } unit symbols for the dropdown
 *   material   the lookup material (units + density), for converting a working-unit entry
 *   isSaving   disables Add / Save while the mutation is in flight
 *   onSubmit   (quantityInDeclarationUnit) => void
 */
export const PhysicalBalanceModal = ({
  open,
  onClose,
  row,
  units,
  material,
  isSaving,
  onSubmit,
}) => {
  const t = useTranslations();
  const [convert, { isFetching: isConverting }] = useLazyConvertUnitsQuery();

  // Kept through the close so the caller clearing its row can't empty the dialog mid-exit.
  const shownRow = useRef(row);
  if (open && row) shownRow.current = row;
  const activeRow = shownRow.current ?? EMPTY_ROW;
  const isUpdate = activeRow.hasPhysicalBalance;

  const [unitType, setUnitType] = useState(UNIT_TYPES.declaration);
  const [quantity, setQuantity] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setUnitType(UNIT_TYPES.declaration);
    setQuantity(isUpdate ? String(activeRow.physicalClosingBalanceRaw ?? '') : '');
    setError(null);
  }, [open, isUpdate, activeRow]);

  // The working unit is offered only when the server can convert it: a cross-category pair needs a density.
  const canUseWorking = useMemo(() => {
    if (!material?.workingUnit || !units?.working) return false;
    const pairNeedsDensity = needsDensity(
      normalizeUnit(material.declarationUnit),
      normalizeUnit(material.workingUnit),
    );
    return !pairNeedsDensity || Boolean(material.density);
  }, [material, units]);

  const unitOptions = useMemo(
    () => [
      {
        value: UNIT_TYPES.declaration,
        label: units?.declaration ?? '',
        badgeText: t('UNIT_DECLARATION'),
      },
      ...(canUseWorking
        ? [{ value: UNIT_TYPES.working, label: units.working, badgeText: t('UNIT_WORKING') }]
        : []),
    ],
    [t, units, canUseWorking],
  );

  const handleUnitChange = useCallback((event) => {
    setUnitType(event.target.value);
    setError(null);
  }, []);
  const handleQuantityChange = useCallback((event) => {
    setQuantity(event.target.value);
    setError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!REGEX.POSITIVE_DECIMAL.test(quantity)) {
      setError(t('INVENTORY_PHYSICAL_BALANCE_QUANTITY_INVALID'));
      return;
    }
    if (unitType === UNIT_TYPES.declaration) {
      onSubmit(Number(quantity));
      return;
    }
    const conversion = await convert({
      material: {
        declarationUnit: normalizeUnit(material.declarationUnit),
        workingUnit: normalizeUnit(material.workingUnit),
      },
      unit: normalizeUnit(material.workingUnit),
      quantity,
      density: material.density ? String(material.density) : null,
    });
    const converted = conversion.data?.declarationQuantity;
    if (converted == null) {
      setError(t('INVENTORY_PHYSICAL_BALANCE_CONVERSION_FAILED'));
      return;
    }
    onSubmit(Number(converted));
  }, [quantity, unitType, material, convert, onSubmit, t]);

  const buttons = useMemo(
    () => [
      { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: onClose },
      {
        label: isUpdate ? t('COMMON_SAVE') : t('COMMON_ADD'),
        variant: ButtonVariants.primary,
        onClick: handleSubmit,
        disabled: isSaving || isConverting,
        loading: isSaving || isConverting,
      },
    ],
    [t, isUpdate, onClose, handleSubmit, isSaving, isConverting],
  );

  const unitLabel = useMemo(
    () => (
      <span className={styles.labelWithInfo}>
        {t('FIELD_UNIT')}
        <Tooltip content={t('INVENTORY_PHYSICAL_BALANCE_UNIT_INFO')}>
          <Info size={16} aria-hidden="true" />
        </Tooltip>
      </span>
    ),
    [t],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(
        isUpdate
          ? 'INVENTORY_PHYSICAL_BALANCE_UPDATE_TITLE'
          : 'INVENTORY_PHYSICAL_BALANCE_ADD_TITLE',
      )}
      buttons={buttons}
    >
      <div className={styles.body}>
        <div className={styles.period}>
          <Typography
            as={TypographyElements.span}
            variant={TypographyVariants.bodyXs}
            weight={TypographyWeights.semibold}
            color={TypographyColors.secondary}
          >
            {t('INVENTORY_COL_RECONCILIATION_PERIOD')}
          </Typography>
          <div className={styles.periodRow}>
            <TextInput value={formatDate(activeRow.from)} disabled aria-label={t('COMMON_FROM')} />
            <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.primary}>
              -
            </Typography>
            <TextInput value={formatDate(activeRow.to)} disabled aria-label={t('COMMON_TO')} />
          </div>
        </div>
        <Dropdown
          label={unitLabel}
          required
          value={unitType}
          onChange={handleUnitChange}
          options={unitOptions}
        />
        <TextInput
          label={t('FIELD_QUANTITY')}
          placeholder={t('FORM_QUANTITY_PLACEHOLDER')}
          required
          type="number"
          value={quantity}
          onChange={handleQuantityChange}
          status={error ? InputStatuses.error : InputStatuses.default}
          message={error}
        />
      </div>
    </Modal>
  );
};
